import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Crown, Clock, Users, Wifi, WifiOff, RefreshCw, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useRematchSystem } from "@/hooks/useRematchSystem";
import { Position, PieceType, getAllLegalMoves, getGameStatus } from "@/lib/chess-engine";
import { formatTime } from "@/lib/clock-utils";
import { normalizeGameState } from "@/lib/gameStateUtils";
import { usePlayerProfiles } from "@/hooks/usePlayerProfiles";
import { AvatarDisplay } from "./AvatarDisplay";
import { NativeChessBoard } from "./NativeChessBoard";
import { GameControls } from "./GameControls";
import { CapturedPieces } from "./CapturedPieces";
import { VoiceNoteRecorder } from "./VoiceNoteRecorder";

interface MultiplayerChessBoardProps {
  room: any;
  gameSession: any;
  participants: any[];
  playerColor: 'white' | 'black' | null;
  isConnected: boolean;
  onMove: (from: Position, to: Position) => Promise<boolean>;
  onLeaveRoom: () => void;
  onResign?: () => void;
  onOfferDraw?: () => void;
}


export function MultiplayerChessBoard({
  room,
  gameSession,
  participants,
  playerColor,
  isConnected,
  onMove,
  onLeaveRoom,
  onResign,
  onOfferDraw
}: MultiplayerChessBoardProps) {
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [validMoves, setValidMoves] = useState<Position[]>([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [lastMoveNumber, setLastMoveNumber] = useState(0);
  const { toast } = useToast();
  
  // Use unified rematch system
  const { rematchState, offerRematch, acceptRematch, declineRematch } = 
    useRematchSystem(gameSession?.id || null);

  // Fetch player profiles
  const playerIds = [gameSession?.white_player_id, gameSession?.black_player_id];
  const { profiles } = usePlayerProfiles(playerIds);

  // Early return if room is null
  if (!room) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-6">
          <CardContent className="text-center">
            <p className="text-muted-foreground">No room data available</p>
            <Button onClick={onLeaveRoom} variant="outline" className="mt-4">
              Back to Menu
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Update current time every 100ms for live clock
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Calculate live clock times
  const getLiveClockTimes = () => {
    if (!gameSession || !gameSession.white_time_ms || !gameSession.black_time_ms) {
      return { whiteTimeMs: 0, blackTimeMs: 0 };
    }

    const now = currentTime;
    const lastTick = gameSession.last_tick_at ? new Date(gameSession.last_tick_at).getTime() : now;
    const elapsed = now - lastTick;

    let whiteTimeMs = gameSession.white_time_ms;
    let blackTimeMs = gameSession.black_time_ms;

    // Subtract elapsed time from current player if game is ongoing
    if (gameSession.game_status === 'ongoing' && !gameSession.clocks_paused) {
      if (gameSession.current_turn === 'white') {
        whiteTimeMs = Math.max(0, whiteTimeMs - elapsed);
      } else {
        blackTimeMs = Math.max(0, blackTimeMs - elapsed);
      }
    }

    return { whiteTimeMs, blackTimeMs };
  };

  const { whiteTimeMs, blackTimeMs } = getLiveClockTimes();

  const gameState = gameSession?.game_state ? normalizeGameState(gameSession.game_state) : null;
  const isPlayerTurn = gameState && playerColor === gameSession?.current_turn;

  const handleSquareClick = async (position: Position) => {
    console.log('Square clicked:', position, 'Player:', playerColor, 'Turn:', gameSession?.current_turn, 'Is player turn:', isPlayerTurn);
    
    if (!gameState || !playerColor) {
      console.log('No game state or player color');
      return;
    }

    const [row, col] = position;
    const piece = gameState.board[row][col];
    console.log('Piece at clicked position:', piece);

    // Priority 1: If clicking on a piece of player's color, always select it (even if another piece is selected)
    if (piece && piece.color === playerColor) {
      console.log('Selecting piece of player color:', piece);
      setSelectedSquare(position);
      const moves = getAllLegalMoves(gameState).filter(move => 
        move.from[0] === row && move.from[1] === col
      ).map(move => move.to);
      console.log('Valid moves:', moves);
      setValidMoves(moves);
      return;
    }

    // Only proceed with move logic if a piece is already selected
    if (selectedSquare) {
      const [selectedRow, selectedCol] = selectedSquare;
      const selectedPiece = gameState.board[selectedRow][selectedCol];

      // If clicking on same square, deselect
      if (selectedRow === row && selectedCol === col) {
        console.log('Deselecting same square');
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }

      // If valid move and it's player's turn, execute it
      if (validMoves.some(([r, c]) => r === row && c === col)) {
        if (!isPlayerTurn) {
          console.log('Not player turn, cannot make move');
          toast({
            title: "Not Your Turn",
            description: "Wait for your opponent to make their move",
            variant: "destructive"
          });
          return;
        }
        console.log('Executing move from', selectedSquare, 'to', position);
        const success = await onMove(selectedSquare, position);
        if (success) {
          setSelectedSquare(null);
          setValidMoves([]);
          
          // Show voice recorder after player's move
          if (gameSession && gameSession.move_count > lastMoveNumber) {
            setLastMoveNumber(gameSession.move_count);
            setShowVoiceRecorder(true);
          }
        }
        return;
      }

      // If clicking on invalid square, keep current selection (don't deselect)
      console.log('Clicked on invalid square, keeping current selection');
    } else {
      console.log('No piece selected and clicked on empty square or opponent piece');
    }
  };


  const status = gameState ? getGameStatus(gameState) : null;
  const whitePlayer = participants.find(p => p.player_color === 'white');
  const blackPlayer = participants.find(p => p.player_color === 'black');
  
  // Get player profiles
  const whiteProfile = gameSession?.white_player_id ? profiles[gameSession.white_player_id] : null;
  const blackProfile = gameSession?.black_player_id ? profiles[gameSession.black_player_id] : null;

  // Waiting for second player (only show if no game session exists)
  if (participants.length < 2 && !gameSession) {
    return (
      <div className="space-y-6">
        <Card className="border-accent/50">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Users className="h-5 w-5" />
              Waiting for Opponent
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Room Code</p>
              <p className="font-mono text-2xl font-bold text-accent">{room.room_code}</p>
              {room.room_name && (
                <p className="text-sm font-medium text-foreground mt-2">{room.room_name}</p>
              )}
            </div>
            
            <div className="flex items-center justify-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-success" />
                  <span className="text-sm text-success">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive">Disconnected</span>
                </>
              )}
            </div>

            <p className="text-muted-foreground">
              Share the room code with your opponent to start playing!
            </p>

            <div className="flex gap-2">
              <Button 
                variant="hero" 
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(room.room_code);
                  toast({
                    title: "Room Code Copied! 📋",
                    description: "Share this code with your friend",
                  });
                }}
              >
                Copy Code
              </Button>
            </div>

            <Button variant="outline" onClick={onLeaveRoom}>
              Leave Room
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state if we have participants but no game state yet
  if (!gameState) {
    return (
      <div className="space-y-6">
        <Card className="border-accent/50">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Users className="h-5 w-5" />
              Setting Up Game
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {room.room_name && (
              <div className="bg-muted p-3 rounded-lg mb-4">
                <p className="font-medium text-foreground">{room.room_name}</p>
                <p className="text-sm text-muted-foreground">Room Code: {room.room_code}</p>
              </div>
            )}
            <p className="text-muted-foreground">
              {participants.length === 2 ? 
                'Both players connected. Starting game...' : 
                'Waiting for game session to initialize...'
              }
            </p>
            <div className="flex items-center justify-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-success" />
                  <span className="text-sm text-success">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive">Disconnected</span>
                </>
              )}
            </div>
            <Button variant="outline" onClick={onLeaveRoom}>
              Leave Room
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Game Header */}
      <Card className="border-accent/50">
        <CardHeader className="pb-4">
          <CardTitle className="text-center flex items-center justify-center gap-2">
            <Crown className="h-5 w-5 text-accent" />
            {room.room_name ? room.room_name : `Private Room: ${room.room_code}`}
          </CardTitle>
          {room.room_name && (
            <p className="text-center text-sm text-muted-foreground">Room Code: {room.room_code}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-success" />
                  <span className="text-sm text-success">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive">Reconnecting...</span>
                </>
              )}
            </div>
            
            <Badge variant={status?.status === 'checkmate' ? "destructive" : "default"}>
              {status?.status || "Game in Progress"}
            </Badge>
            
            <div className="flex gap-2">
              {(gameSession?.game_status === 'checkmate' || gameSession?.game_status === 'draw' || gameSession?.game_status === 'stalemate') && (
                <div className="flex-1 space-y-2">
                  {/* Rematch Section */}
                  {!rematchState.hasOffer && !rematchState.rematchSessionId && (
                    <Button 
                      variant="default" 
                      size="sm"
                      className="w-full"
                      onClick={offerRematch}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Offer Rematch
                    </Button>
                  )}

                  {rematchState.hasOffer && rematchState.isOfferer && !rematchState.rematchSessionId && (
                    <Alert>
                      <Clock className="h-4 w-4" />
                      <AlertTitle>Rematch Offered</AlertTitle>
                      <AlertDescription>
                        Waiting for opponent to accept...
                      </AlertDescription>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2 w-full"
                        onClick={declineRematch}
                      >
                        Cancel Offer
                      </Button>
                    </Alert>
                  )}

                  {rematchState.hasOffer && !rematchState.isOfferer && !rematchState.rematchSessionId && (
                    <Alert className="border-primary">
                      <Users className="h-4 w-4" />
                      <AlertTitle>Rematch Requested!</AlertTitle>
                      <AlertDescription>
                        Your opponent wants to play again with swapped colors
                      </AlertDescription>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          variant="default" 
                          size="sm"
                          className="flex-1"
                          onClick={acceptRematch}
                        >
                          Accept
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="flex-1"
                          onClick={declineRematch}
                        >
                          Decline
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {rematchState.rematchSessionId && (
                    <Alert variant="default">
                      <CheckCircle className="h-4 w-4" />
                      <AlertTitle>Rematch Started!</AlertTitle>
                      <AlertDescription>
                        A new game has begun with swapped colors
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={onLeaveRoom}>
                Leave Room
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Player Info */}
      <div className="grid grid-cols-2 gap-4">
        {/* Black Player (top when viewing as white) */}
        <Card className={`border-2 ${playerColor === 'white' ? 'order-1' : 'order-2'} ${gameSession?.current_turn === 'black' ? 'border-accent' : 'border-border'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {blackProfile && (
                  <AvatarDisplay
                    username={blackProfile.username || blackProfile.display_name}
                    avatarConfig={blackProfile.avatar_config}
                    avatarUrl={blackProfile.avatar_url}
                    size="sm"
                  />
                )}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-foreground rounded-full"></div>
                    <span className="font-medium">
                      {blackProfile ? 
                        (blackProfile.display_name || blackProfile.username || 'Black Player') : 
                        (blackPlayer ? 'Black Player' : 'Waiting...')
                      }
                      {playerColor === 'black' && ' (You)'}
                    </span>
                  </div>
                  {blackProfile && (
                    <span className="text-xs text-muted-foreground">
                      ELO: {blackProfile.elo_rating}
                    </span>
                  )}
                </div>
              </div>
              <div className={`flex items-center gap-2 p-2 rounded transition-colors ${
                gameSession?.current_turn === 'black' && gameSession?.game_status === 'ongoing' 
                  ? 'bg-primary/20 text-primary' : 'text-muted-foreground'
              }`}>
                <Clock className="h-4 w-4" />
                <span className={`font-mono ${blackTimeMs < 30000 ? 'text-destructive' : ''}`}>
                  {formatTime(blackTimeMs)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* White Player (bottom when viewing as white) */}
        <Card className={`border-2 ${playerColor === 'white' ? 'order-2' : 'order-1'} ${gameSession?.current_turn === 'white' ? 'border-accent' : 'border-border'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {whiteProfile && (
                  <AvatarDisplay
                    username={whiteProfile.username || whiteProfile.display_name}
                    avatarConfig={whiteProfile.avatar_config}
                    avatarUrl={whiteProfile.avatar_url}
                    size="sm"
                  />
                )}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-background border-2 border-foreground rounded-full"></div>
                    <span className="font-medium">
                      {whiteProfile ? 
                        (whiteProfile.display_name || whiteProfile.username || 'White Player') : 
                        (whitePlayer ? 'White Player' : 'Waiting...')
                      }
                      {playerColor === 'white' && ' (You)'}
                    </span>
                  </div>
                  {whiteProfile && (
                    <span className="text-xs text-muted-foreground">
                      ELO: {whiteProfile.elo_rating}
                    </span>
                  )}
                </div>
              </div>
              <div className={`flex items-center gap-2 p-2 rounded transition-colors ${
                gameSession?.current_turn === 'white' && gameSession?.game_status === 'ongoing'
                  ? 'bg-primary/20 text-primary' : 'text-muted-foreground'
              }`}>
                <Clock className="h-4 w-4" />
                <span className={`font-mono ${whiteTimeMs < 30000 ? 'text-destructive' : ''}`}>
                  {formatTime(whiteTimeMs)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Layout Responsive Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 w-full max-w-7xl mx-auto">
        {/* Captured Pieces - Left Side (Hidden on Mobile) */}
        <div className="hidden lg:block lg:col-span-1 space-y-4">
          {gameState && playerColor && participants.length === 2 && (
            <CapturedPieces gameState={gameState} playerColor={playerColor} />
          )}
        </div>

        {/* Chess Board - Center (Full width on mobile) */}
        <div className="col-span-1 lg:col-span-3">
          <div className="w-full max-w-lg mx-auto lg:max-w-none">
            <NativeChessBoard
              gameState={gameState}
              onSquareClick={handleSquareClick}
              highlightedSquares={validMoves}
              selectedSquare={selectedSquare}
              flipped={playerColor === 'black'}
            />
          </div>
          
          {/* Mobile Captured Pieces (Below board on mobile) */}
          <div className="block lg:hidden mt-4">
            {gameState && playerColor && participants.length === 2 && (
              <CapturedPieces gameState={gameState} playerColor={playerColor} />
            )}
          </div>
        </div>

        {/* Game Controls - Right Side */}
        <div className="col-span-1 lg:col-span-1">
          {gameState && participants.length === 2 && (onResign || onOfferDraw) && (
            <GameControls
              onResign={onResign}
              onOfferDraw={onOfferDraw}
              compact={true}
            />
          )}
        </div>
      </div>
      
      {/* Voice Recorder - Full Width Below Grid */}
      {showVoiceRecorder && gameSession && (
        <div className="mt-6">
          <VoiceNoteRecorder
            gameSessionId={gameSession.id}
            moveNumber={gameSession.move_count}
            prompt="What were you thinking with that move?"
            onSave={() => setShowVoiceRecorder(false)}
            onCancel={() => setShowVoiceRecorder(false)}
            className="animate-in fade-in-50 slide-in-from-bottom-4 duration-300"
          />
        </div>
      )}
    </div>
  );
}
